import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import { CopiedGrid } from '../../orderState.types';
import { OrderProductGridItem } from '../orderProducts.types';
import { OrderProductForGridContainer } from '../product/grid/components/orderProductGrid/components/grid/types/orderProductGridContainer.types';
import { useGridStoreApiOptional } from '../product/grid/store/GridStoreProvider';
import { produceItemsToStore } from '../product/grid/store/produceItemsToStore';
import { formatNewGrid } from '../product/utils/productGrid.utils';

interface UseCopyPasteParams extends UsedInOfflineMode {
  product: OrderProductForGridContainer;
  grid: OrderProductGridItem[];
  copiedGrid: CopiedGrid;
  orderId?: string;
  resetSelectedPacks: () => void;
  onPasted?: (combinationKey: string) => void;
}

const useCopyPasteGrid = ({
  copiedGrid,
  grid,
  product,
  orderId,
  resetSelectedPacks,
  onPasted,
  isOfflineMode = false,
}: UseCopyPasteParams) => {
  const gridStoreApi = useGridStoreApiOptional();
  const combinationKey = product.combinationKey;

  const onClickPaste = useCallback(async (): Promise<void> => {
    if (!orderId || !gridStoreApi || !combinationKey) {
      return;
    }

    const newGrid = formatNewGrid(product, grid, copiedGrid.grid);
    const items = newGrid.map((item) => ({
      ...item,
      orderId,
      id: item.id ?? (isOfflineMode ? uuidv4() : undefined),
    }));

    produceItemsToStore({ api: gridStoreApi, combinationKey, items });
    resetSelectedPacks();
    onPasted?.(combinationKey);
  }, [
    onPasted,
    grid,
    product,
    orderId,
    isOfflineMode,
    copiedGrid.grid,
    combinationKey,
    gridStoreApi,
    resetSelectedPacks,
  ]);

  return {
    onClickPaste,
  };
};

export default useCopyPasteGrid;
